import { UserSearchViewModel } from './UserSearchViewModel'
import SearchableUserStore from '../iam/SearchableUserStore'
import { action, computed, makeObservable, observable } from 'mobx'
import { Id } from '../types/definitions'
import { getFeatureToggleClient } from '../infra/featureToggles'
import { GetCurrentUserWorkspaceMemberships } from '../iam/WorkspaceMembershipStore'

export interface UserColumn {
  id: string
  header: string
  modifyVisibility: boolean
  sortable: boolean
  visible: boolean
}

const UserManagementColumns: Array<UserColumn> = [
  {
    id: 'name',
    header: 'Name',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'email',
    header: 'Email',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'userType',
    header: 'User Type',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'lastLogin',
    header: 'Last Login',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
  {
    id: 'dateCreated',
    header: 'Date Created',
    modifyVisibility: true,
    visible: true,
    sortable: false,
  },
]

export const enum SelectionState {
  /**
   * Nothing is selected at all.
   */
  NoneSelected = 'NoneSelected',
  /**
   * Some are selected.
   */
  SomeSelected = 'SomeSelected',
  /**
   * All are selected.
   */
  AllSelected = 'AllSelected',
}

export class UserManagementViewModel {
  @observable
  private selectedUsers = new Set<Id>()

  userSearch = new UserSearchViewModel(this.store)

  columns = UserManagementColumns

  constructor(
    private store: SearchableUserStore,
    private getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships
  ) {
    makeObservable(this)
  }

  isSelected = (userId: Id) => this.selectedUsers.has(userId)

  activate = () => this.userSearch.activate()

  deactivate() {
    this.userSearch.deactivate()
  }

  @computed
  get selectedState() {
    if (this.selectedUsers.size === 0) {
      return SelectionState.NoneSelected
    } else if (this.users.length === this.selectedUsers.size) {
      return SelectionState.AllSelected
    } else {
      return SelectionState.SomeSelected
    }
  }

  @computed
  get canAccessUserManagement() {
    // TODO: remember to check for ADMIN_GLOBAL_USER_LIST when WS is implemented.
    return (
      getFeatureToggleClient().variation('WorkPortal.UserManagement', false) &&
      this.getCurrentUserWorkspaceMemberships().some((wms) =>
        wms.member.hasPermission('ADMIN_USER_LIST')
      )
    )
  }

  @computed
  get users() {
    return this.userSearch.scrollProvider.items
  }

  @action.bound
  toggleUser(checked: boolean, userId: Id) {
    if (checked) {
      this.selectedUsers.add(userId)
    } else {
      this.selectedUsers.delete(userId)
    }
  }

  @action.bound
  toggleAll(checked: boolean) {
    if (checked) {
      this.selectedUsers = new Set(this.users.map((user) => user.publicId))
    } else {
      this.selectedUsers.clear()
    }
  }
}
